const post = {
  meta: {
    slug: "true-cost-of-merchant-cash-advance",
    title: "The True Cost of a Merchant Cash Advance (and When It's Still Worth It)",
    excerpt:
      "MCAs get a bad reputation — often deservedly. But there are five situations where the math actually works. Here's the honest breakdown.",
    date: "2025-12-20",
    readTime: "7 min read",
    category: "Buyer's guide",
    image:
      "https://images.pexels.com/photos/12471781/pexels-photo-12471781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  body: `
## What an MCA actually is

A **merchant cash advance** isn't technically a loan. It's the sale of your future revenue at a discount. A funder gives you a lump sum today in exchange for a fixed dollar amount they'll collect from your future receipts, typically over 4 to 18 months.

The pricing is quoted as a **factor rate**, not an APR. A 1.35 factor on a $100,000 advance means you'll pay back $135,000. A 1.49 factor means $149,000. Repayment happens automatically — a small percentage of your daily card sales, or a fixed daily/weekly ACH debit from your operating account, until the fixed amount is paid off.

## Why "factor rate" makes it look cheaper than it is

A 1.35 factor "sounds like" 35% — an ugly but comprehensible number. It isn't.

Because MCAs are repaid quickly (often 6–12 months), the **effective APR** is much higher than the factor rate implies. That $100,000 advance paid back at $135,000 over 12 months has an effective APR of roughly **63%**. Paid back over 8 months, closer to **95%**. Paid back over 6 months, north of **125%**.

The industry quotes factor rates for a reason. APR would scare people. So brokers who genuinely work for you will always translate a factor into APR before you sign anything.

## The five situations where an MCA still makes sense

Despite the pricing, there are real cases where an MCA is the right — or only — product. All five have a common trait: **the opportunity or risk you're solving is bigger than the cost of the capital.**

### 1. Opportunity capital with a fast, provable return

You're a restaurant with a 3-week window to lock in a lease on a second location that would double revenue. A bank loan will take 45 days. If the second location will do $30K/month in additional profit, borrowing $150K at a 1.4 factor to close the deal in a week is math that works. **The interest costs less than the missed opportunity.**

### 2. Damage control on a business that would otherwise close

Your walk-in freezer died on Saturday. You need $22K by Wednesday or you lose a week of revenue and half your inventory. An MCA is the only thing that funds in 48 hours. **The cost of the advance is less than the cost of a week of closure.**

### 3. Bridge to closing capital

Your SBA loan approval came in but won't fund for another 30 days. Payroll is next Friday. A short MCA — with a written plan to pay it off in full from the SBA proceeds — can be a legitimate bridge, if (and only if) you prepay before the daily draws compound the effective rate.

### 4. Credit-repair capital for a rebound

You're two years past a bankruptcy or a bad breakup with a lender. Your revenue is real and clean but no bank will touch you yet. An MCA at 1.35 factor, paid on time for 8 months, is one of the few ways to rebuild business credit *and* buy the inventory you need to keep growing. This one requires discipline — the plan has to include *not renewing* after payoff.

### 5. Cash-basis businesses banks won't underwrite

Some legitimate businesses — trucking, staffing agencies, some retail — get declined by banks because their profit margins look thin on paper even when cash flow is strong. Bank statements tell the real story. MCA underwriting reads bank statements. Sometimes it's simply the only product that reads your business honestly.

## The five situations where an MCA is a mistake

- **Refinancing cheap existing debt** — never move a 12% bank loan onto a 60% APR MCA
- **Buying long-lived assets** — real estate, machinery, vehicles. Match the term of the loan to the life of the asset. A 5-year piece of equipment financed with an 8-month MCA is malpractice.
- **General "growth" you can't tie to a return** — if you can't state precisely which invoice, contract, or line item the money will produce, you're gambling with expensive chips
- **Stacking** — taking a second (or third) MCA while an existing one is still being repaid. The combined daily debit will strangle cash flow. Once you're stacked, the exit becomes very expensive.
- **Habitual reliance** — one MCA is a tool. Six MCAs in five years is a symptom.

## The three questions that separate a real MCA from a trap

Before you sign, insist on answers to these three questions. In writing.

1. **"What is the effective APR on this advance, given the expected repayment schedule?"** Not the factor rate. The APR. A legitimate broker will calculate it and hand it to you.
2. **"What is the prepayment discount if I pay off early?"** Some MCA funders give a real discount for early payoff. Some don't. Some *say* they do but structure the contract so the "discount" is illusory. Get the actual math — a genuine early-payoff quote.
3. **"Will this advance be stacked on top of any existing advance?"** If yes, walk away. A responsible broker will not put you into a stack.

## The bottom line

MCAs are a legitimate product for a specific set of situations — mostly speed and severity. But they're the most expensive form of capital in the small-business market, and they punish two habits harshly: **using them for the wrong purpose, and not paying them off when you should.**

Used as a scalpel, they save businesses. Used as a hammer, they end them. At Steady Path Funding, we place MCAs only when the math genuinely wins for the borrower — and we've talked more owners out of them than into them. If a broker is pushing you into one without walking you through the alternatives, that's your answer.
`,
};

export default post;
